/**
 * Tax arithmetic for buying add-ons against an existing booking.
 *
 * pricing_rules[].price (and booking_items.unit_price) carry GROSS amounts in
 * tax-inclusive mode (EU/VAT) and NET amounts in tax-exclusive mode (US sales
 * tax). The customer must always be charged gross, and bookings.total_tax must
 * move with the add-ons, so every caller that charges for or commits add-ons
 * needs the same three deltas:
 *
 *   itemsDelta — sum of add-on prices in the unit they are stored in
 *                (gross when inclusive, net when exclusive); this is what
 *                bookings.subtotal moves by
 *   taxDelta   — the tax portion: embedded in itemsDelta when inclusive,
 *                added on top of it when exclusive; bookings.total_tax moves by it
 *   grossDelta — what the customer is charged and what bookings.total_amount
 *                moves by
 *
 * Mirrors the delta arithmetic in EditFlow.jsx and the gross-up in
 * api/manage/extension-options.js / extend-booking.js.
 *
 * tax_rate is stored as a decimal (0.20 = 20%) — do NOT divide by 100.
 * A NULL tax_included is treated as inclusive, matching the server helpers.
 */

export const round2 = (n) => Math.round(n * 100) / 100;

export function getTaxConfig(booking) {
  return {
    taxRate: parseFloat(booking?.tax_rate) || 0,
    taxIncluded: booking?.tax_included !== false,
  };
}

export function computeAddonDelta(booking, addonsList) {
  const { taxRate, taxIncluded } = getTaxConfig(booking);
  const itemsDelta = (addonsList || []).reduce(
    (sum, a) => sum + (parseFloat(a.price) || 0) * (Number(a.quantity) || 0),
    0
  );
  const taxDelta = taxIncluded
    ? (taxRate > 0 ? (itemsDelta * taxRate) / (1 + taxRate) : 0)
    : itemsDelta * taxRate;
  const grossDelta = taxIncluded ? itemsDelta : itemsDelta + taxDelta;
  return { taxRate, taxIncluded, itemsDelta, taxDelta, grossDelta };
}

/** Gross amount for one add-on line, for display. */
export function grossAddonLine(booking, addon) {
  return computeAddonDelta(booking, [addon]).grossDelta;
}
